/**
 * Token Manager for PWA
 * Handles automatic token refresh, expiration tracking, and background sync
 */

import { authStorage } from './authStorage';

class TokenManager {
  constructor() {
    this.refreshTimer = null;
    this.checkTimer = null; // Periodic check timer
    this.refreshPromise = null;
    this.refreshListeners = new Set();
    this.isRefreshing = false;
    this.refreshBufferSeconds = 300; // Refresh 5 minutes before expiration
    this.checkInterval = 60000; // Check every minute
    this.refreshCallback = null;
  }

  /**
   * Add listener for token refresh events
   */
  onTokenRefresh(callback) {
    this.refreshListeners.add(callback);
    return () => this.refreshListeners.delete(callback);
  }

  /**
   * Notify all listeners of token refresh
   */
  notifyListeners(newToken) {
    this.refreshListeners.forEach(callback => {
      try {
        callback(newToken);
      } catch (error) {
        console.error('[TokenManager] Error in refresh listener:', error);
      }
    });
  }

  /**
   * Calculate time until token expiration
   */
  async getTimeUntilExpiration() {
    const expiresAt = await authStorage.getTokenExpiration();
    if (!expiresAt) return null;

    const now = Date.now();
    const timeUntilExpiration = expiresAt - now;
    return Math.max(0, timeUntilExpiration);
  }

  /**
   * Schedule automatic token refresh
   */
  async scheduleRefresh(refreshCallback) {
    // Clear existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    const timeUntilExpiration = await this.getTimeUntilExpiration();
    if (!timeUntilExpiration) {
      console.warn('[TokenManager] No expiration time, cannot schedule refresh');
      return;
    }

    // Calculate refresh time (buffer seconds before expiration)
    const refreshTime = timeUntilExpiration - (this.refreshBufferSeconds * 1000);

    if (refreshTime <= 0) {
      // Token expires soon, refresh immediately
      console.log('[TokenManager] Token expires soon, refreshing immediately');
      this.refresh(refreshCallback);
      return;
    }

    console.log(`[TokenManager] Scheduled refresh in ${Math.round(refreshTime / 1000)}s`);

    this.refreshTimer = setTimeout(async () => {
      await this.refresh(refreshCallback);
      // Schedule next refresh
      await this.scheduleRefresh(refreshCallback);
    }, refreshTime);
  }

  /**
   * Refresh token
   */
  async refresh(refreshCallback) {
    // Prevent concurrent refresh attempts
    if (this.isRefreshing) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = (async () => {
      try {
        const token = await authStorage.getToken();
        if (!token) {
          throw new Error('No token to refresh');
        }

        console.log('[TokenManager] Refreshing token...');
        const newToken = await refreshCallback(token);

        if (newToken) {
          this.notifyListeners(newToken);
          // Schedule next refresh
          await this.scheduleRefresh(refreshCallback);
        }

        return newToken;
      } catch (error) {
        console.error('[TokenManager] Token refresh failed:', error);
        // Clear auth data on refresh failure
        await authStorage.clear();
        throw error;
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * Check if token needs refresh
   */
  async needsRefresh() {
    return await authStorage.isTokenExpiredOrExpiringSoon(this.refreshBufferSeconds);
  }

  /**
   * Stop automatic refresh
   */
  stop() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    if (this.checkTimer) {
      clearInterval(this.checkTimer);
      this.checkTimer = null;
    }
    this.refreshListeners.clear();
    this.refreshCallback = null;
  }

  /**
   * Check and refresh if needed (for periodic checking)
   */
  async checkAndRefresh() {
    if (!this.refreshCallback) return;
    
    // Don't refresh if already in progress
    if (this.isRefreshing) {
      return;
    }

    // Check if token needs refresh
    if (await this.needsRefresh()) {
      await this.refresh(this.refreshCallback);
    }
  }

  /**
   * Start token management with refresh callback
   */
  async start(refreshCallback) {
    this.refreshCallback = refreshCallback;
    
    // Check if token needs immediate refresh
    if (await this.needsRefresh()) {
      console.log('[TokenManager] Token needs refresh, refreshing now...');
      await this.refresh(refreshCallback);
    } else {
      // Schedule automatic refresh
      await this.scheduleRefresh(refreshCallback);
    }

    // Start periodic checking (every minute)
    this.checkTimer = setInterval(() => {
      this.checkAndRefresh();
    }, this.checkInterval);
  }
}

// Singleton instance
export const tokenManager = new TokenManager();

